import type { Document } from "@contentful/rich-text-types";
import { RichTextRenderer } from "@scott/contentful/rich-text-renderer";
import { cva, type VariantProps } from "class-variance-authority";
import { Image } from "../../elements/image/image";
import { Dash } from "../../elements/text-d2c/dash";
import { TextD2C } from "../../elements/text-d2c/text";
import { Column, Container, Row } from "../../layout/grid";
import { cn } from "../../lib/utils";
import type { QuoteInspectorProps } from "./quote.preview";

const quoteVariants = cva("", {
  variants: {
    background: {
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
      black: "bg-black",
    },
  },
  defaultVariants: {
    background: "white",
  },
});

export interface QuoteCite {
  name: string;
  team: string;
  image?: {
    src: string;
    srcSet?: string;
    alt?: string;
  };
}

export interface QuoteProps extends VariantProps<typeof quoteVariants> {
  sys?: { id: string; locale?: string };
  variant?: "mission" | "athlete";
  background?: "white" | "whitesmoke" | "black";
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  quote: { json: Document };

  /** Label displayed above the dash in the mission variant (e.g. "Mission") */
  label?: string;
  /** Attribution displayed below the quote in the athlete variant */
  cite?: QuoteCite;
  inspectorProps?: QuoteInspectorProps;
}

export function Quote({
  variant = "mission",
  background = "white",
  paddingTop,
  paddingBottom,
  quote,
  label,
  cite,
  inspectorProps,
}: Readonly<QuoteProps>) {
  const bgClasses = quoteVariants({ background });
  const color = background === "black" ? "white" : "black";

  return (
    <Container
      className={cn(bgClasses, "component-quote")}
      fullWidth={true}
      paddingBottom={paddingBottom}
      paddingTop={paddingTop}
      testId="quote"
      {...inspectorProps?.container}
    >
      <Row className="gap-0">
        <Column className="col-span-10 col-start-2 md:col-span-8 md:col-start-3">
          <figure className="m-0">
            {variant === "mission" && label && (
              <>
                <TextD2C color={color} variant="pillar">
                  {label}
                </TextD2C>
                <Dash color={color} variant="h6" />
              </>
            )}

            <blockquote className="m-0">
              <TextD2C
                color={color}
                variant="p-xlarge"
                {...inspectorProps?.quote}
              >
                <RichTextRenderer richText={quote.json} />
              </TextD2C>
            </blockquote>

            {variant === "athlete" && cite && (
              <figcaption className="mt-8 flex items-center gap-4">
                <cite className="flex items-center gap-4 not-italic">
                  {cite.image && (
                    <Image
                      alt={cite.image.alt ?? cite.name}
                      aspectRatio="1:1"
                      borderRadius="full"
                      className="!h-15 !w-15 shrink-0"
                      sizes="60px"
                      src={cite.image.src}
                      srcSet={cite.image.srcSet}
                    />
                  )}
                  <div className="leading-none">
                    <span
                      className={cn(
                        "mb-1 block font-brand font-normal font-semibold text-[12px] leading-none tracking-[4%]",
                        color === "white" ? "text-white" : "text-black"
                      )}
                    >
                      {cite.name}
                    </span>
                    <span className="mb-0 block font-brand font-normal font-semibold text-[12px] text-blazeorange leading-none tracking-[4%]">
                      {cite.team}
                    </span>
                  </div>
                </cite>
              </figcaption>
            )}
          </figure>
        </Column>
      </Row>
    </Container>
  );
}
